// Which heading each section draws. The page renderer decides from section order and section data (not by counting in
// the DOM) so the server render and hydration agree. A page has one H1:
//  1. a v2 post or project carries its own (so every hero is an H2), and so does a post header with a title;
//  2. otherwise the first hero with a title;
//  3. otherwise the first section, in page order, that draws a visible main heading (sectionH1Index).
import {s2UsesV2} from "./s2Pick.js";
import {V2_REFRESH_LAYOUTS} from "./v2Layouts.js";

const OWNS_H1 = new Set(["post-v2", "project-v2"]);

const filled = (v) => typeof v === "string" && v.trim() !== "";
const list = (v) => (Array.isArray(v) ? v : []);

function ownsH1(s) {
    if (OWNS_H1.has(s?.type)) return true;
    return s?.type === "post-header" && filled(s.title);
}

export function heroHeadingLevels(sections) {
    const items = Array.isArray(sections) ? sections : [];
    let taken = items.some(ownsH1);
    return items.map((s) => {
        if (s?.type !== "hero") return 2;
        if (taken || !filled(s.title)) return 2;
        taken = true;
        return 1;
    });
}

// Types whose main heading is `title` (blank = no heading drawn), in both their older and v2 components.
const TITLE_TYPES = new Set(["content", "split", "features", "grid", "testimonial", "showcase", "contact-details", "document-header"]);
// The v2 bands: `heading`, else `title`.
const HEADING_OR_TITLE_TYPES = new Set(["story", "video", "case-study", "compare", "accordion-photo", "estimator", "tabs", "bento", "rail"]);

// Does this section draw a visible main heading (the one the section's frame or component sets in H1 or H2)? Decided
// from the section alone. Sections that fill from the business's own data (hours, reviews, team, latest posts, prices,
// shop lists...) can draw nothing when that data is empty, so they never take the H1.
export function hasMainHeading(section) {
    const s = section || {};
    const t = s.type;
    if (TITLE_TYPES.has(t)) return filled(s.title);
    if (HEADING_OR_TITLE_TYPES.has(t)) return filled(s.heading) || filled(s.title);
    if (t === "image-grid") return s2UsesV2(s) && filled(s.title);
    if (t === "promise") return true;
    if (t === "quote-form") return filled(s.heading ?? "Get a quote");
    // Today's types: the older component draws `title`; with a v2 layout the v2 component may add a default heading.
    if (V2_REFRESH_LAYOUTS[t]) {
        const v2 = V2_REFRESH_LAYOUTS[t].includes(s.layout);
        if (!v2) return filled(s.title) && (t !== "links" || list(s.links).length > 0);
        switch (t) {
            case "contact": return true;
            case "faq": return list(s.items).some((i) => i && filled(i.question));
            case "links": return filled(s.title) && list(s.links).some((l) => l && l.link);
            case "download": return list(s.attachments).some((f) => f && f.url);
            case "areas": return list(s.areas).some((a) => filled(typeof a === "string" ? a : a?.name)) || filled(s.content);
            default: return filled(s.title); // cta
        }
    }
    return false;
}

// The index of the section that takes the page's H1 when no hero and no post or project does, or -1.
export function sectionH1Index(sections) {
    const items = Array.isArray(sections) ? sections : [];
    if (items.some(ownsH1)) return -1;
    if (items.some((s) => s?.type === "hero" && filled(s.title))) return -1;
    return items.findIndex((s) => s?.type !== "hero" && hasMainHeading(s));
}

// One level per section (1 or 2): hero levels as before, and the picked section gets 1.
export function pageHeadingLevels(sections) {
    const levels = heroHeadingLevels(sections);
    const at = sectionH1Index(sections);
    if (at >= 0) levels[at] = 1;
    return levels;
}
